import { type HTMLAttributes, type ReactNode } from 'react';
import styles from './section-heading.module.css';

export interface SectionHeadingProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Bölüm başlığı metni; başlık ailesi, ana metin renginde, gerçek `h2`. */
  readonly title: ReactNode;
  /**
   * Görsel ölçek (yeni görünüm Aşama 1b): `h3` (varsayılan, 22 / 28 600,
   * telefonda 20 / 26) ya da `h2` (32 / 38 700, telefonda 26 / 32). Etiket
   * (`titleAs`) ölçekten bağımsızdır. Büyük harfli üst etiket yoktur.
   */
  readonly scale?: 'h2' | 'h3';
  /**
   * Bölümün İLKİ mi? Üst boşluk, dolgu ve ayırıcı düşer. Kap zaten DOM'da ilk
   * çocuksa CSS `:first-child` aynı işi yapar; bu prop araya bant/uyarı giren
   * yerler ve kart İÇİ bölümler içindir (kartın dolgusu ayrımı zaten kurar).
   */
  readonly first?: boolean;
  /** Başlık satırının sağ ucu — sayaç, "tümünü gör", "temizle". */
  readonly trailing?: ReactNode;
  /** Başlığın önündeki işaret (öneri kıvılcımı). Anlam taşımaz, gizlenir. */
  readonly icon?: ReactNode;
  /** `aria-labelledby` bağlamak için başlığın kimliği. */
  readonly titleId?: string;
  /**
   * Başlığın etiketi. Varsayılan `h2`. `h1` yalnız sayfanın KENDİ başlığı bu
   * bölüm başlığıysa verilir (ayrı bir poster başlığı olmayan alt sayfa, ör.
   * kurumun "Geçmiş etkinlikler" listesi). Görünüm değişmez: `.title` taban
   * katmanın `h1` ölçüsünü tamamen ezer.
   */
  readonly titleAs?: 'h1' | 'h2';
}

/**
 * BÖLÜM BAŞLIĞI (web) — başlık + ayırıcı + dikey ritim TEK YERDE.
 *
 * Karar: `docs/design/tipografi/WarmBreak Tipografi - Bolum Basligi ve Ust
 * Etiket.SPEC.md` (14.09.2026). Sözleşme `@icebreaker/shared/design`ten gelir
 * ve CSS'e `theme-css.ts` üzerinden `--ib-section-*` olarak iner; bu dosya
 * ölçü YAZMAZ, yalnız değişkenleri kullanır.
 *
 * MOBİLDEN FARKI BİLİNÇLİ: mobil kap İÇERİĞİ de sarar (RN'de `:first-child`
 * yok, ritim elle kurulur); web'de içerik kardeş kalır ve sıfırlama CSS'in
 * kendi `:first-child`inden gelir. İki platform aynı SÖZLEŞMEYİ paylaşır,
 * aynı ağaç biçimini değil.
 *
 * SEMANTİK: bölüm başlığı gerçek `h2`dir. Sayfa başlığı `h1`, kart başlığı
 * `h3` kalır — üst etiket (`kicker`) `span`/`p` olmayı sürdürür, çünkü anlam
 * değil bağlam taşır.
 */
export function SectionHeading({
  title,
  first = false,
  trailing,
  icon,
  titleId,
  titleAs: Title = 'h2',
  scale = 'h3',
  className,
  ...rest
}: SectionHeadingProps) {
  return (
    <div
      {...rest}
      className={[
        styles.row,
        first ? styles.first : '',
        scale === 'h2' ? styles.scaleH2 : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-section-scale={scale}
    >
      <Title className={styles.title} id={titleId}>
        {icon ? (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        ) : null}
        {title}
      </Title>
      {trailing ? <div className={styles.trailing}>{trailing}</div> : null}
    </div>
  );
}

export const sectionHeadingStyles = styles;
